import type { ReactNode } from "react";

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { OptionSelect } from "@/components/ui/option-select";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

/**
 * The app's page navigation: first, previous, a window of numbers, next, last.
 *
 * One component rather than one per screen. It used to be the list pager in
 * `resource-page.tsx` and a second, differently drawn one on the Yellow Page —
 * worded Prev/Next buttons, its own ellipsis, its own idea of how wide a
 * current page should be — so two screens in the same app paged in two
 * different shapes. Everything that varies between the callers is a prop; what
 * a page number looks like is not one of them.
 */

/**
 * The run of page numbers around the current one.
 *
 * `size` is how many the row has room for: five where there is width, three
 * where there is not, none on a frame narrow enough that the numbers would
 * push the arrows off the end. The current page is always inside the window —
 * a pager that cannot show you where you are is a pager with no anchor.
 */
export function pageWindow(current: number, pages: number, size = 5): number[] {
  if (size <= 0 || pages <= 0) return [];
  const span = Math.min(size, pages);
  const start = Math.max(1, Math.min(current - Math.floor(span / 2), pages - span + 1));
  return Array.from({ length: span }, (_, index) => start + index);
}

/**
 * One page number. The current page is filled, so "where am I" is answered
 * before "where can I go".
 */
export function PageButton({
  label,
  active,
  children,
  ...props
}: React.ComponentProps<"button"> & { label: string; active?: boolean }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={cn(
        // Square at one digit and wider from there. It was a fixed 32px, which
        // a four-digit page number could not fit: 1677 spilled out of its own
        // button and into the two beside it.
        "text-subhead grid h-8 min-w-8 place-items-center rounded-lg px-2 tabular-nums",
        "transition-colors duration-[var(--duration-fast)]",
        "disabled:pointer-events-none disabled:opacity-35",
        active
          ? "bg-accent text-accent-fg font-medium"
          : "text-fg-muted hover:bg-surface-inset hover:text-fg",
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function PageNav({
  page,
  pages,
  onChange,
  disabled = false,
  tight = false,
  jumpToEnd = true,
  label,
  className,
}: {
  page: number;
  /** How many there are. One or none draws nothing. */
  pages: number;
  onChange: (page: number) => void;
  /** Held while a read is in the air, so a second page cannot be asked for. */
  disabled?: boolean;
  /**
   * Three numbers instead of five, and no jump to the first page. For a frame
   * that has the arrows but not the width for a full window.
   */
  tight?: boolean;
  /**
   * Whether the last page can be reached in one press. A list paged deep
   * enough that the server will not count that far leaves it off: a greyed
   * button that never becomes available is furniture.
   */
  jumpToEnd?: boolean;
  /** Names the landmark — "Pagination" unless a screen has a better word. */
  label?: string;
  className?: string;
}) {
  const t = useTranslation();
  // Whether a one-page list draws a pager at all is the caller's to decide: a
  // table keeps its row so the count and the size picker beside it stay put,
  // a card grid drops it because there is nothing else on that line.
  const window = pageWindow(page, pages, tight ? 3 : 5);
  return (
    <nav
      aria-label={label ?? t("common.pagination")}
      className={cn("flex items-center gap-1", className)}
    >
      {tight ? null : (
        <PageButton
          label={t("common.firstPage")}
          disabled={disabled || page === 1}
          onClick={() => onChange(1)}
        >
          <ChevronsLeft className="size-4" aria-hidden />
        </PageButton>
      )}
      <PageButton
        label={t("common.previousPage")}
        disabled={disabled || page === 1}
        onClick={() => onChange(page - 1)}
      >
        <ChevronLeft className="size-4" aria-hidden />
      </PageButton>
      {window.map((number) => (
        <PageButton
          key={number}
          label={t("rp.pageN").replace("{n}", String(number))}
          active={number === page}
          disabled={disabled}
          onClick={() => onChange(number)}
        >
          {number}
        </PageButton>
      ))}
      {/* The last page, kept in view when the window stops short of it — with
          an ellipsis to say the run is not continuous. */}
      {jumpToEnd && window.at(-1) !== pages && pages > 0 ? (
        <>
          <span className="text-fg-subtle px-1" aria-hidden>
            …
          </span>
          <PageButton
            label={t("rp.pageN").replace("{n}", String(pages))}
            disabled={disabled}
            onClick={() => onChange(pages)}
          >
            {pages}
          </PageButton>
        </>
      ) : null}
      <PageButton
        label={t("common.nextPage")}
        disabled={disabled || page === pages}
        onClick={() => onChange(page + 1)}
      >
        <ChevronRight className="size-4" aria-hidden />
      </PageButton>
      {jumpToEnd ? (
        <PageButton
          label={t("common.lastPage")}
          disabled={disabled || page === pages}
          onClick={() => onChange(pages)}
        >
          <ChevronsRight className="size-4" aria-hidden />
        </PageButton>
      ) : null}
    </nav>
  );
}

/** The page sizes every list offers. */
export const SIZES = [10, 25, 50, 100];

/** Below this the numbered window drops to three and the first-page jump goes. */
export const PAGER_TIGHT = 620;
/** Below this the row cannot hold a window at all and becomes two lines. */
export const PAGER_BARE = 440;

/**
 * The whole pagination row: how many rows a page holds and how many there are,
 * on the left; where you are in them, on the right.
 *
 * The bar, not only the numbers — the Yellow Page had a count of its own in
 * its own words and no size picker at all, so the one thing a person does at
 * the bottom of a list was a different control on that screen than on every
 * other. A screen with nothing to offer but the numbers leaves `size` out and
 * gets the nav alone.
 */
export function Pager({
  page,
  pages,
  onChange,
  size,
  onSize,
  total,
  sizes = SIZES,
  disabled = false,
  tight = false,
  bare = false,
  from,
  to,
  jumpToEnd = true,
  label,
  className,
}: {
  page: number;
  pages: number;
  onChange: (page: number) => void;
  /** How many rows a page holds. Left out, the left-hand half is not drawn. */
  size?: number;
  onSize?: (size: number) => void;
  /**
   * How many rows there are, as the screen writes it — a list whose count
   * stopped at the cap says "10,000+", which is not a number.
   */
  total?: ReactNode;
  sizes?: readonly number[];
  disabled?: boolean;
  tight?: boolean;
  /**
   * Two lines instead of one: the count above, Prev and Next at the edges
   * below. For a frame too narrow to hold a window of numbers at all.
   */
  bare?: boolean;
  /** The rows this page covers, for the range `bare` reads out. */
  from?: number;
  to?: number;
  jumpToEnd?: boolean;
  label?: string;
  className?: string;
}) {
  const t = useTranslation();
  if (bare)
    return (
      /*
       * The phone pager. The one-line layout wants a range, a size picker and
       * a row of numbered buttons on 320px, and something always lost: the
       * numbers pushed the arrows off the end, and dropping them left the page
       * number crushed between two chevrons.
       *
       * So it is two lines here. The first says where you are in the list and
       * how big a page is — the reading half, no controls that move you. The
       * second is only the moving: Prev and Next pushed out to the edges where
       * thumbs reach, the page number between them in the accent, because on a
       * row of two buttons the one thing that is not pressable is the one that
       * has to say so.
       *
       * No first/last jumps and no numbered window. Both are ways of crossing
       * the list quickly, and neither survives at this width — the range on the
       * line above is what says how far there is to go.
       */
      <div
        className={cn(
          "hairline-t text-fg-muted text-subhead flex shrink-0 flex-col gap-3 px-4 py-3",
          className,
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="tabular-nums">
            {t("rp.range")
              .replace("{from}", (from ?? 0).toLocaleString())
              .replace("{to}", (to ?? 0).toLocaleString())
              // The wide row can be handed anything to draw as a total; this
              // one puts it inside a sentence, so only a word or a number.
              .replace(
                "{total}",
                typeof total === "string" || typeof total === "number" ? String(total) : "",
              )}
          </span>
          {/* "25 ⌄ / page" reads as one phrase, so the picker is `bare`: the
              number and its chevron, with no box drawn round them. A field
              shell here would make the size look like a form control that had
              wandered into the count. */}
          {size === undefined ? null : (
            <span className="flex shrink-0 items-center gap-1">
              <OptionSelect
                bare
                fieldSize="sm"
                aria-label={t("common.rowsPerPage")}
                value={String(size)}
                onChange={(next) => onSize?.(Number(next))}
                clearable={false}
                choices={sizes.map((option) => ({
                  value: String(option),
                  label: String(option),
                }))}
              />
              {t("rp.perPage")}
            </span>
          )}
        </div>
        {/* Three columns rather than `justify-between`: the outer two are
            equal whatever the two labels measure, so the page number sits on
            the centre of the card and not on the midpoint between whichever
            words this locale uses. */}
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <Button
            className="justify-self-start"
            variant="secondary"
            size="sm"
            aria-label={t("common.previousPage")}
            disabled={disabled || page === 1}
            onClick={() => onChange(page - 1)}
          >
            <ChevronLeft aria-hidden />
            {t("rp.prev")}
          </Button>
          <span className="text-accent text-subhead font-medium tabular-nums">
            {t("rp.pageN").replace("{n}", String(page))}
          </span>
          <Button
            className="justify-self-end"
            variant="secondary"
            size="sm"
            aria-label={t("common.nextPage")}
            disabled={disabled || page === pages}
            onClick={() => onChange(page + 1)}
          >
            {t("rp.next")}
            <ChevronRight aria-hidden />
          </Button>
        </div>
      </div>
    );
  return (
    <div
      className={cn(
        "hairline-t text-fg-muted text-subhead flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3",
        // The count holds the left edge and the numbers the right. With no
        // count there is nothing to hold the left, so the numbers take it
        // rather than floating in the middle of an empty row.
        size === undefined ? "justify-end" : "justify-between",
        className,
      )}
    >
      {size === undefined ? null : (
        <span className="flex items-center gap-2">
          {t("rp.showing")}
          <OptionSelect
            fieldSize="sm"
            aria-label={t("common.rowsPerPage")}
            value={String(size)}
            onChange={(next) => onSize?.(Number(next))}
            // A page always holds some number of rows, so there is nothing to
            // clear this back to.
            clearable={false}
            className="w-20"
            choices={sizes.map((option) => ({
              value: String(option),
              label: String(option),
            }))}
          />
          {t("common.of")} <span className="text-fg tabular-nums">{total}</span>
        </span>
      )}
      <PageNav
        page={page}
        pages={pages}
        onChange={onChange}
        disabled={disabled}
        tight={tight}
        jumpToEnd={jumpToEnd}
        {...(label === undefined ? {} : { label })}
      />
    </div>
  );
}
